import { useEffect } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import { useMutation } from '@tanstack/react-query'
import { Button } from '$components'
import { Card } from '$components'
import { appointmentPublicConfirmMutation } from '$api-client/@tanstack/react-query.gen'
import { Text } from '$components'
import { CheckIcon, WarningIcon, CalendarIcon, LogoIcon } from '$components'
import { ToastHelper } from '$components'

export function ConfirmAppointmentPage() {
  const { id } = useParams<{ id: string }>()
  const [searchParams] = useSearchParams()

  const signature = searchParams.get('signature')
  const expires = searchParams.get('expires')

  const { mutate: confirmAppointment, isPending, isSuccess, isError } = useMutation({
    ...appointmentPublicConfirmMutation(),
    onError: (error) => ToastHelper.error(error?.message || 'Failed to confirm appointment. Please contact the business.'),
  })

  const handleConfirm = () => {
    if (!id || !signature || !expires) {
      ToastHelper.error('Invalid or expired link')
      return
    }

    confirmAppointment({
      path: { id: Number(id) },
      body: {
        signature: signature,
        expires: Number(expires),
      },
    })
  }

  useEffect(() => {
    if (!signature || !expires) {
      ToastHelper.error('Invalid or expired link')
    }
  }, [signature, expires])

  const isInvalidLink = !signature || !expires

  return (
    <div className="min-h-screen bg-surface-muted flex items-center justify-center p-stack-roomy">
      <div className="w-full max-w-prose-md">
        {/* Logo/Branding */}
        <div className="flex justify-center mb-stack-section">
          <LogoIcon className="h-6 w-auto text-fg-link" />
        </div>

        {/* Main Card */}
        <Card>
          {!isSuccess && !isError && (
            <Card.Content className="p-stack-grid sm:p-stack-hero border border-border rounded-card">
              <div className="flex flex-col items-center text-center mb-stack-grid">
                <CalendarIcon className="size-10 text-fg-link mb-stack-section" />

                <Text
                  type="h2"
                  className="text-fg mb-stack"
                >
                  Confirm Your Appointment
                </Text>

                <Text
                  type="s"
                  className="text-fg-subtle max-w-prose-sm"
                >
                  Please confirm your attendance to let us know you&apos;ll be there. This helps us prepare for your visit.
                </Text>
              </div>

              <Button
                onClick={handleConfirm}
                isLoading={isPending}
                disabled={isPending || isInvalidLink}
                variant="primary"
                className="w-full h-12 text-body"
              >
                {isPending ? 'Confirming...' : 'Confirm Appointment'}
              </Button>
            </Card.Content>
          )}

          {isSuccess && (
            <Card.Content className="p-stack-grid sm:p-stack-hero flex flex-col border border-border items-center text-center rounded-card">
              <CheckIcon className="size-10 text-success mb-stack-section" />

              <Text
                type="h2"
                className="text-fg mb-stack"
              >
                All Set!
              </Text>

              <Text
                type="s"
                className="text-fg-subtle max-w-prose-sm mb-stack-grid"
              >
                Your appointment has been successfully confirmed. We look forward to seeing you!
              </Text>
            </Card.Content>
          )}

          {isError && (
            <Card.Content className="p-stack-grid sm:p-stack-hero flex flex-col border border-border items-center text-center rounded-card">
              <WarningIcon className="size-10 text-danger mb-stack-section" />

              <Text
                type="h2"
                className="text-fg mb-stack"
              >
                Unable to Confirm
              </Text>

              <Text
                type="s"
                className="text-fg-subtle max-w-prose-sm mb-stack-grid"
              >
                We couldn&apos;t confirm your appointment. Please contact the business directly for assistance.
              </Text>
            </Card.Content>
          )}
        </Card>

        {/* Footer Text */}
        <div className="text-center">
          <Text
            type="s"
            className="text-fg-subtle"
          >
            This is a secure confirmation link. Do not share it with others.
          </Text>
        </div>
      </div>
    </div>
  )
}
